import APIConfig from "../config/apiConfig";
import APIService from "service/network/APIService";
import { DOC_TOKEN_STORAGE_KEY } from "../helper/documentConstants";

/**
 * The patient record header's service: the Patient-details dialog's write, and
 * the "All documents" menu's list / upload / edit / doc token.
 *
 * BRIDGE-OWNED, and deliberately a subset of phr's PatientService. The vendored
 * components that import `../service/PatientService` — PatientDetailsDialog,
 * and the header's documents menu (DocumentsMenu, UploadDocumentModal) — keep
 * that path, class name and method signatures, so the methods they call are
 * phr's, line for line, against the module-local config. Everything else phr's
 * service carries (the multiprofile search, assessments) belongs to screens
 * bridge does not have.
 *
 * The patient RECORD's read is not here: both owners already load it through
 * bridge's own PatientsService.getPatient, the same GET {base}/patients/{id}
 * phr uses.
 */
class PatientService {
  constructor() {
    this.apiService = new APIService();
  }

  /**
   * PUT /v1/patients/profile/update — a FULL-RECORD replace (go-user-service
   * UpdatePatientRecords). The body is built by the dialog's
   * buildProfileUpdateBody; any field left out is cleared server-side. When a
   * dateOfBirth is sent the server recomputes `age` from it, so the echoed age
   * cannot go stale. The response is not read: owners re-GET the record.
   */
  updatePatientProfile(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.updateProfileInfo;

    return this.apiService.put(url, body, onResponse, onError);
  }

  /**
   * The patient's WORK email, for the Patient-details dialog — shown there,
   * never edited (deactivating it is a Bridge Profile action).
   * api-client-config-service GET /v1/account/patient/work-email/patient/{pid}:
   * { workEmail, status (1 active, 2 deactivated), accountCode, … }, or 404
   * "no work email found for patient" when there is none.
   */
  getWorkEmail(patientId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getWorkEmail.replace("{0}", patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * ALL of the patient's documents, grouped by type — bridge's getAllDocuments
   * (getByPatientDoc). NOT appointment-scoped: this is every document the patient
   * has, which is what the header's "View all" shows. Responds
   * [{ type, count, all: [document] }].
   */
  getPatientDocuments(patientId, onResponse, onError) {
    const url = APIConfig.documentApis.getByPatientDoc.format(patientId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Who prescribed each of the patient's prescription PDFs — prescription-service's doctors
   * list, PDFs only: [{ id, pdfId, doctorDetails: { name } }], newest first. One call names
   * every prescription in the documents lists (helper/usePrescriptionDoctors).
   */
  getPrescriptionDoctors(patientId, onResponse, onError) {
    const url = `${APIConfig.prescription.doctors.format(patientId)}?hasPdf=true`;

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Move one document into another folder (bridge's DocumentService.updateType).
   *
   * `type` is the NUMERIC folder id, straight from DOC_TYPE_OPTIONS — not a
   * canonicalType()'d one. That fold is a read-path guarantee for the legacy
   * prescriptions type, and the server does it again on save
   * (document-service controllers/document.go:421-423), so folding here would
   * only hide which folder the operator actually picked.
   *
   * Responds with the whole updated document, including the `typeTitle` the
   * server re-derives.
   */
  updateDocumentType(documentId, type, onResponse, onError) {
    const url = APIConfig.documentApis.updateType.format(documentId);

    return this.apiService.patch(url, { type }, onResponse, onError);
  }

  /**
   * Rename / re-describe one document (bridge's updateNameAndDescription).
   *
   * BOTH FIELDS ARE ALWAYS SENT, even when only one was edited: the handler
   * assigns each unconditionally (controllers/document.go:343-344), so leaving
   * `description` out would blank a description the operator never touched.
   * That is why this takes the pair rather than a partial patch object.
   */
  updateDocumentNameAndDescription(documentId, { title, description }, onResponse, onError) {
    const url = APIConfig.documentApis.updateName.format(documentId);

    return this.apiService.patch(
      url,
      { title: String(title || ""), description: String(description || "") },
      onResponse,
      onError
    );
  }

  /**
   * Mint + cache the doc token the file URLs need (bridge's saveNewDocToken).
   * Stored under the shared DOC_TOKEN_STORAGE_KEY (zy.bridge.doc.token) so
   * buildDocUrl can append it synchronously at click time (a token fetch on
   * click would trip the popup blocker).
   *
   * Only a real token is written: that key is shared, and bridge's legacy
   * documents screen JSON.parses it unguarded, so a 204's `undefined` would
   * break more than this menu. Best-effort: after a failed mint, files open
   * with whatever token the key already held (or none).
   */
  generateDocToken(onResponse, onError) {
    const url = APIConfig.documentApis.tokenGenerate;

    return this.apiService.get(
      url,
      (res) => {
        if (res && res.token) {
          try {
            localStorage.setItem(DOC_TOKEN_STORAGE_KEY, JSON.stringify(res));
          } catch (e) {
            /* storage disabled — files just open untokenised */
          }
        }
        if (onResponse) onResponse(res);
      },
      onError
    );
  }

  /**
   * Upload one document to the PATIENT's own library — the header's
   * "All documents" upload (Figma 6326:6687).
   *
   * Deliberately NOT AppointmentService.uploadDocument: that one hard-codes
   * `title: fileName`, which would silently throw away the title the operator
   * typed and file the document under its file name instead.
   *
   * THE MULTIPART SHAPE, from document-service controllers/document.go:157-176:
   *   file        REQUIRED. 422 without it.
   *   document    REQUIRED, a JSON STRING. Appending the object directly
   *               serialises to "[object Object]" and the server stores an
   *               empty record.
   *   patientId   REQUIRED as a form field TOO, and it is NOT the same field as
   *               the one inside `document`: the form value only chooses the
   *               storage folder, while the row's patient comes solely from the
   *               JSON. They are sent from one argument here so they cannot
   *               drift apart — and `document.patientId` must be a NUMBER and
   *               must be present, because the handler type-switches on it and
   *               panics into a 500 on nil.
   *   fileName    Optional; the server falls back to the file's own name.
   *
   * TWO FIELDS BRIDGE SENDS THAT WE DO NOT, both verified inert rather than
   * assumed so:
   *   patientPhoneNo  never read by the handler at all.
   *   notifyPatient   parsed, then gated behind `userResponse.ID > 0` where
   *                   `userResponse` is a zero-value struct that is never
   *                   assigned — the branch has been unreachable since the
   *                   lookup was refactored out. Sending true notifies nobody.
   * Please do not "restore parity" by adding them back.
   */
  uploadPatientDocument(patientId, file, meta, onResponse, onError) {
    const form = new FormData();
    const pid = Number(patientId);

    form.append("file", file);
    form.append("fileName", meta.fileName);
    form.append(
      "document",
      JSON.stringify({
        patientId: pid,
        title: meta.title,
        description: meta.description || "",
        type: meta.type,
        format: meta.format,
        status: meta.status,
      })
    );
    form.append("patientId", String(pid));

    return this.apiService.postWithFormData(
      APIConfig.documentApis.uploadDoc,
      form,
      onResponse,
      onError
    );
  }
}

export default PatientService;
